<template>
  <InputGroup class="w-100">
    <InputGroupAddon v-if="$slots['prepend'] != null" class="p-0">
      <slot name="prepend" />
    </InputGroupAddon>
    <div v-if="inline" class="inline-multi-select" :class="{ 'p-disabled': disabled }">
      <div class="inline-multi-select__filter">
        <IconField>
          <InputText
            v-model="searchString"
            class="w-100"
            :disabled="disabled"
            :placeholder="t('components.shared.searchSelect.TreeSelect.search')"
          />
          <InputIcon class="pi pi-search" />
        </IconField>
      </div>
      <div class="inline-multi-select__options" role="listbox" aria-multiselectable="true">
        <InputRow
          v-for="option in filteredInlineOptions"
          :key="option.id"
          :model-value="getOptionState(option.id) === 'selected'"
          type="checkbox"
          class="inline-multi-select__option"
          :class="{ 'inline-multi-select__option--emphasized': option.emphasized }"
          :disabled="disabled || option.disabled"
          role="option"
          small
          :aria-selected="getOptionState(option.id) !== undefined"
          :display-name="option.label"
          :name="option.id.toString()"
          @update:model-value="toggleInlineOption(option.id)"
        />
        <div v-if="loading" class="inline-multi-select__empty">
          <i class="fas fa-spinner fa-spin" aria-hidden="true" />
        </div>
        <div v-else-if="filteredInlineOptions.length === 0" class="inline-multi-select__empty">
          {{ t("components.shared.searchSelect.BaseSelect.noResultPossibleString") }}
        </div>
      </div>
    </div>
    <MultiSelect
      v-else
      ref="multiSelect"
      v-model="modelValue"
      :options="displayOptions"
      option-label="label"
      option-value="id"
      :option-group-label="optionGroups == null ? undefined : 'label'"
      :option-group-children="optionGroups == null ? undefined : 'group'"
      :placeholder="placeholder"
      :show-clear="showClear"
      :disabled="disabled"
      fluid
      filter
      filter-icon="fas fa-search"
      clear-icon="fas fa-times"
      :filter-locale="locale"
      :reset-filter-on-hide="true"
      :scroll-height="scrollHeight"
      :virtual-scroller-options="{
        itemSize: getRemSize() * 2.155,
      }"
      :class="state == null ? undefined : state ? 'p-valid' : 'p-invalid'"
      :auto-filter-focus="true"
      :auto-option-focus="false"
      :data-testid="dataTestid"
      :pt="{
        pcFilter: {
          root: {
            'data-testid': `${dataTestid}|Filter`,
          },
        },
        overlay: {
          'data-testid': `${dataTestid}|Overlay`,
        },
      }"
      :empty-message="t('components.shared.searchSelect.BaseSelect.noResultPossibleString')"
      :show-toggle-all="true"
      :max-selected-labels="3"
      display="chip"
      chip-icon="fas fa-times-circle"
      @before-show="handleOpen"
      @filter="searchString = $event.value"
      @hide="($emit('close'), (searchString = ''))"
      @change="closeAfterSelect"
    >
      <template #option="{ option }">
        <span
          class="flex align-items-center gap-2"
          :class="{ 'option--emphasized': option.emphasized }"
        >
          <i v-if="option.icon" :class="option.icon" aria-hidden="true" />
          <span>{{ option.label }}</span>
        </span>
      </template>
      <template v-if="valueOverlayLabel != null" #value>
        <span class="value-overlay-label">{{ valueOverlayLabel }}</span>
      </template>
      <template v-else #chip="{ value, removeCallback }">
        <Chip
          class="gap-1 m-0"
          :style="{
            backgroundColor: options.find((opt) => opt.id === value)?.color,
            color: getContrastColor(options.find((opt) => opt.id === value)?.color),
          }"
          :pt="{
            removeIcon: {
              style: {
                color: `${getContrastColor(options.find((opt) => opt.id === value)?.color)} !important`,
              },
            },
          }"
          :label="options.find((opt) => opt.id === value)?.label ?? t('global.deleted')"
          :removable="showClear"
          @remove="removeCallback($event, value)"
        >
        </Chip>
      </template>
      <template #footer>
        <div v-if="allowCreate" class="px-3 pt-1 pb-2 text-xs">
          <T
            v-if="searchString.length > 0"
            :key="searchString"
            key-name="components.lSelect.wantToCreate"
            ns="app"
          >
            <template #searchString>
              <a
                href="#"
                :data-testid="`${dataTestid}|CreateNew`"
                @click.prevent="$emit('create', searchString)"
                >{{ searchString || "" }}</a
              >
            </template>
          </T>
          <a v-else> &nbsp; </a>
        </div>
      </template>
    </MultiSelect>
    <InputGroupAddon
      v-if="linkToOrigin != null && !hideJumpToOrigin"
      class="p-0"
      :class="state == null ? undefined : state ? 'p-valid' : 'p-invalid'"
    >
      <LISAButton
        variant="link"
        class="w-full h-full border-0"
        severity="secondary"
        icon="fas fa-arrow-right"
        :disabled="loading || modelValue == null || modelValue.length === 0 || disabled"
        @click="jumpToOriginClicked"
      />
      <Popover ref="buttonPopover">
        <div
          class="flex flex-column gap-1"
          style="width: min(50vw, 20rem); max-height: 40vh; overflow-y: auto"
        >
          <div
            v-for="(value, index) in modelValue"
            :key="`Entries${index}`"
            class="flex justify-content-between align-items-center"
          >
            <div class="text-truncate">
              {{ optionMap.get(value) }}
            </div>
            <LISAButton
              :disabled="!isOptionDisabled(value) || disabled"
              size="small"
              icon="fas fa-arrow-right"
              @click="
                $router.push({
                  ...linkToOrigin,
                  params: { ...linkToOrigin.params, id: value },
                })
              "
            />
          </div>
        </div>
      </Popover>
    </InputGroupAddon>
  </InputGroup>
</template>

<script setup lang="ts">
import MultiSelect from "primevue/multiselect";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import { useTranslation } from "@/language/translation";
import T from "@/language/StaticTranslation";
import LISAButton from "../../primevue/LISAButton.vue";
import { useRouter, type RouteLocationNamedRaw } from "vue-router";
import { ref, useTemplateRef } from "vue";
import { computed } from "vue";
import Popover from "primevue/popover";
import type { MapResultEntry, MultiSelectOptionState } from "../SelectHelper";
import { getContrastColor } from "@/functions/colorHelper";
import Chip from "primevue/chip";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputRow from "../../form/InputRow.vue";
import { buildOptionGroups, snapshotSelectedIds } from "./MultiSelectOptionGrouping";

const modelValue = defineModel({
  type: Array as () => number[],
  required: false,
  default: null,
});

const props = withDefaults(
  defineProps<{
    options: MapResultEntry[];
    showClear?: boolean;
    loading: boolean;
    disabled?: boolean;
    dataTestid?: string;
    linkToOrigin?: RouteLocationNamedRaw;
    allowCreate?: boolean;
    placeholder?: string;
    state?: boolean | null;
    hideJumpToOrigin?: boolean;
    valueOverlayLabel?: string;
    closeOnSelect?: boolean;
    groupSelectedOptions?: boolean;
    inline?: boolean;
    optionStates?: Record<number, MultiSelectOptionState>;
  }>(),
  {
    dataTestid: undefined,
    linkToOrigin: undefined,
    placeholder: undefined,
    closeOnSelect: false,
    valueOverlayLabel: undefined,
    groupSelectedOptions: true,
    inline: false,
    state: null,
    optionStates: undefined,
  },
);

const emit = defineEmits<{
  create: [searchString: string];
  open: [];
  close: [];
}>();

const { locale, t } = useTranslation();

const selectedIdsAtOpening = ref(snapshotSelectedIds(modelValue.value));

function prepareOptionsForOpening() {
  selectedIdsAtOpening.value = snapshotSelectedIds(modelValue.value);
}

function handleOpen() {
  prepareOptionsForOpening();
  emit("open");
}

const optionGroups = computed(() =>
  props.groupSelectedOptions
    ? buildOptionGroups(props.options, selectedIdsAtOpening.value, {
        selected: t("components.lMultiSelect.selectedLabel"),
        remaining: t("components.lMultiSelect.remainingLabel"),
      })
    : null,
);

const displayOptions = computed(() => optionGroups.value ?? props.options);

const filteredInlineOptions = computed(() => {
  const normalizedSearch = searchString.value.trim().toLocaleLowerCase(locale.value);
  if (normalizedSearch.length === 0) return props.options;
  return props.options.filter((option) =>
    option.label.toLocaleLowerCase(locale.value).includes(normalizedSearch),
  );
});

function getOptionState(id: number): MultiSelectOptionState | undefined {
  return props.optionStates?.[id] ?? (modelValue.value?.includes(id) ? "selected" : undefined);
}

function toggleInlineOption(id: number) {
  if (props.disabled || props.options.find((option) => option.id === id)?.disabled) return;
  const selected = modelValue.value ?? [];
  modelValue.value = selected.includes(id)
    ? selected.filter((selectedId) => selectedId !== id)
    : [...selected, id];
}

const buttonPopover = useTemplateRef("buttonPopover");
const multiSelect = useTemplateRef("multiSelect");

function closeAfterSelect() {
  if (props.closeOnSelect) {
    multiSelect.value?.hide();
  }
}

function getRemSize(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize);
}

function isOptionDisabled(id: number): boolean {
  if (modelValue.value == null) {
    return false;
  }

  const option = props.options.find((opt) => opt.id === id);
  if (option?.disabled) {
    return true;
  }

  return false;
}
const scrollHeight = computed(() => {
  const groupHeaderCount = optionGroups.value?.length ?? 0;
  const count = Math.min(13, props.options.length + groupHeaderCount);
  return `min(calc(${count * (getRemSize() * 2.155)}px + var(--p-list-gap) * ${count - 1} + 8px), 50vh)`;
});

const optionMap = computed(() => {
  const map = new Map<number, string>();
  props.options.forEach((option) => {
    map.set(option.id, option.label);
  });
  return map;
});

const router = useRouter();

const searchString = ref<string>("");

function jumpToOriginClicked(event: MouseEvent) {
  if (modelValue.value != null && modelValue.value.length === 1) {
    const id = modelValue.value[0];
    if (!isOptionDisabled(id)) {
      router.push({
        ...props.linkToOrigin,
        params: { ...props.linkToOrigin?.params, id: id },
      });
      return;
    }
  }
  buttonPopover.value?.toggle(event);
}
</script>

<style lang="scss" scoped>
.border-radius-right {
  border-top-right-radius: var(--p-border-radius-md);
  border-bottom-right-radius: var(--p-border-radius-md);
}

.value-overlay-label {
  color: var(--p-red-500);
}

.option--emphasized {
  font-weight: 700;
}

.inline-multi-select {
  display: flex;
  width: 100%;
  min-height: 18rem;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--p-form-field-border-color);
  border-radius: var(--p-border-radius-md);
  background: var(--p-form-field-background);
}

.inline-multi-select__filter {
  position: relative;
  padding: 0.75rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.inline-multi-select__options {
  flex: 1;
  max-height: 22rem;
  overflow-y: auto;
  padding: 0.35rem;
}

.inline-multi-select__option {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.65rem;
  border: 0;
  border-radius: var(--p-border-radius-sm);
  background: transparent;
  color: var(--p-text-color);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--p-list-option-focus-background);
  }

  &:disabled {
    cursor: default;
    opacity: var(--p-disabled-opacity);
  }
}

.inline-multi-select__option--emphasized {
  font-weight: 700;
}

.inline-multi-select__empty {
  padding: 1rem;
  color: var(--p-text-muted-color);
  text-align: center;
}
</style>
